import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, List, Shield, RefreshCw, ArrowRight, ChevronRight } from 'lucide-react';
import MainHeader from '../components/MainHeader';
import MainFooter from '../components/MainFooter';

function Home() {
  const scrollToFramework = (e) => {
    e.preventDefault();
    document.getElementById('framework')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <MainHeader />
      
      {/* Section 1 — Hero */}
      <section className="bg-[#102A43] py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-6">
            AI ACCOUNTABILITY STANDARDS COUNCIL
          </p>
          <h1 className="text-[40px] font-bold text-white tracking-[-0.4px] mb-6 leading-tight">
            The Standard for AI Accountability
          </h1>
          <p className="text-[16px] text-[#94A3B8] max-w-[560px] mx-auto mb-10 leading-relaxed">
            Your organization is already using AI. AASC helps you document it, govern it, and prove you're doing it responsibly — before anyone asks.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
            <Link 
              to="/assessment/form" 
              className="bg-[#2F80ED] text-white font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
            >
              Get Your Free AI Accountability Score
            </Link>
            <button 
              onClick={scrollToFramework}
              className="border border-[#2F80ED] text-[#2F80ED] font-semibold px-6 py-3 rounded-[7px] hover:bg-[#2F80ED]/10 transition-colors"
            >
              View the Framework
            </button>
          </div>
          <p className="text-[13px] text-[#64748B]">
            Free · takes under 2 minutes · no signup required
          </p>
        </div>
      </section>

      {/* Section 2 — Trust Bar */}
      <section className="bg-[#0D2236] py-3 px-6">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.8px]">EU AI Act aligned</span>
          <span className="w-1 h-1 rounded-full bg-[#2F80ED]"></span>
          <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.8px]">NIST AI RMF aligned</span>
          <span className="w-1 h-1 rounded-full bg-[#2F80ED]"></span>
          <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.8px]">ISO 42001 aligned</span>
          <span className="w-1 h-1 rounded-full bg-[#2F80ED]"></span>
          <span className="text-[11px] font-bold text-[#64748B] uppercase tracking-[0.8px]">OECD principles aligned</span>
        </div>
      </section>

      {/* Section 3 — The Problem */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
            THE PROBLEM
          </p>
          <h2 className="text-[32px] font-bold text-[#102A43] mb-6 leading-tight">
            AI isn't the risk. Unaccountable AI is.
          </h2>
          <p className="text-[16px] text-[#64748B] leading-relaxed">
            Most organizations are already using AI across their teams. Very little of that use is documented, governed, or auditable. When a client, auditor, procurement team, insurer, or regulator asks how your organization manages AI — what's your answer? AASC gives you the framework, the documentation, and the public proof.
          </p>
        </div>
      </section>

      {/* Section 4 — AI Accountability Score */}
      <section className="bg-[#F9FAFB] py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4 text-center">
            FREE TOOL
          </p>
          <h2 className="text-[32px] font-bold text-[#102A43] mb-3 text-center">
            Know where you stand
          </h2>
          <p className="text-[16px] text-[#64748B] text-center mb-12">
            Six questions. Instant score. Clear next steps.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Card 1 - High Risk */}
            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-[10px] p-6">
              <p className="text-[11px] font-bold text-[#B91C1C] uppercase mb-2">0 – 40</p>
              <h3 className="text-[14px] font-bold text-[#102A43] mb-2">High Accountability Risk</h3>
              <p className="text-[13px] text-[#64748B]">
                Significant governance gaps. Immediate documentation action recommended.
              </p>
            </div>
            
            {/* Card 2 - Developing */}
            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-[10px] p-6">
              <p className="text-[11px] font-bold text-[#92400E] uppercase mb-2">41 – 70</p>
              <h3 className="text-[14px] font-bold text-[#102A43] mb-2">Developing Governance</h3>
              <p className="text-[13px] text-[#64748B]">
                Foundations exist but material gaps remain. A structured plan is needed.
              </p>
            </div>
            
            {/* Card 3 - Ready */}
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-[10px] p-6">
              <p className="text-[11px] font-bold text-[#15803D] uppercase mb-2">71 – 100</p>
              <h3 className="text-[14px] font-bold text-[#102A43] mb-2">Accountability Ready</h3>
              <p className="text-[13px] text-[#64748B]">
                Strong governance posture. Consider pursuing the Declaration.
              </p>
            </div>
          </div>
          
          <div className="text-center">
            <Link 
              to="/assessment/form" 
              className="inline-block bg-[#2F80ED] text-white font-semibold px-8 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors mb-4"
            >
              Get Your Free Score
            </Link>
            <p className="text-[13px] text-[#64748B]">
              Free · takes under 2 minutes · no signup required
            </p>
          </div>
        </div>
      </section>

      {/* Section 5 — The AASC Framework */}
      <section id="framework" className="bg-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4 text-center">
            THE FRAMEWORK
          </p>
          <h2 className="text-[32px] font-bold text-[#102A43] mb-3 text-center">
            A practical path to AI accountability
          </h2>
          <p className="text-[16px] text-[#64748B] text-center mb-12">
            Four levels. Each one builds on the last.
          </p>
          
          <div className="grid md:grid-cols-4 gap-6 mb-12">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#2F80ED] font-bold">1</span>
              </div>
              <h3 className="font-bold text-[#102A43] mb-2">Awareness</h3>
              <p className="text-[14px] text-[#64748B]">
                Understand what AI your organization uses and where.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#2F80ED] font-bold">2</span>
              </div>
              <h3 className="font-bold text-[#102A43] mb-2">Governance</h3>
              <p className="text-[14px] text-[#64748B]">
                Create oversight, approval processes, and documented controls.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#2F80ED] font-bold">3</span>
              </div>
              <h3 className="font-bold text-[#102A43] mb-2">Accountability</h3>
              <p className="text-[14px] text-[#64748B]">
                Build the documentation and audit trails that prove responsible use.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-[#2F80ED]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-[#2F80ED] font-bold">4</span>
              </div>
              <h3 className="font-bold text-[#102A43] mb-2">Declaration</h3>
              <p className="text-[14px] text-[#64748B]">
                Demonstrate your commitment publicly — to clients, partners, and auditors.
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-4 text-[14px]">
            <Link to="/documentation-system" className="text-[#2F80ED] hover:underline flex items-center gap-1">
              Documentation System <span className="text-[#64748B]">(Levels 1–2)</span>
            </Link>
            <span className="text-[#E2E8F0]">|</span>
            <Link to="/assessment-product" className="text-[#2F80ED] hover:underline flex items-center gap-1">
              Assessment <span className="text-[#64748B]">(Level 3)</span>
            </Link>
            <span className="text-[#E2E8F0]">|</span>
            <Link to="/apply" className="text-[#2F80ED] hover:underline flex items-center gap-1">
              Declaration <span className="text-[#64748B]">(Level 4)</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Section 6 — Free Tools */}
      <section className="bg-[#F9FAFB] py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4 text-center">
            FREE TOOLS
          </p>
          <h2 className="text-[32px] font-bold text-[#102A43] mb-3 text-center">
            Start for free — no commitment
          </h2>
          <p className="text-[16px] text-[#64748B] text-center mb-12">
            Four tools. Each one surfaces a gap. The Documentation System closes them.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
              <h3 className="font-bold text-[#102A43] mb-2">AI Accountability Score</h3>
              <p className="text-[14px] text-[#64748B] mb-4">
                Get a scored assessment of your organization's AI accountability posture.
              </p>
              <Link to="/assessment/form" className="text-[#2F80ED] font-medium text-[14px] hover:underline flex items-center gap-1">
                Get Your Score <ArrowRight size={14} />
              </Link>
            </div>
            
            <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
              <h3 className="font-bold text-[#102A43] mb-2">AI Policy Generator</h3>
              <p className="text-[14px] text-[#64748B] mb-4">
                Generate a basic AI Acceptable Use Policy customized for your organization.
              </p>
              <Link to="/tools/policy-generator" className="text-[#2F80ED] font-medium text-[14px] hover:underline flex items-center gap-1">
                Generate Your Policy <ArrowRight size={14} />
              </Link>
            </div>
            
            <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
              <h3 className="font-bold text-[#102A43] mb-2">AI Tool Inventory Builder</h3>
              <p className="text-[14px] text-[#64748B] mb-4">
                Document every AI tool your organization uses in a structured, downloadable register.
              </p>
              <Link to="/tools/inventory-builder" className="text-[#2F80ED] font-medium text-[14px] hover:underline flex items-center gap-1">
                Build Your Inventory <ArrowRight size={14} />
              </Link>
            </div>
            
            <div className="bg-white border border-[#E2E8F0] rounded-[10px] p-6">
              <h3 className="font-bold text-[#102A43] mb-2">AI Governance Checklist</h3>
              <p className="text-[14px] text-[#64748B] mb-4">
                Answer 12 questions and get a gap report showing exactly what your organization is missing.
              </p>
              <Link to="/tools/governance-checklist" className="text-[#2F80ED] font-medium text-[14px] hover:underline flex items-center gap-1">
                Get Your Checklist <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          
          <div className="text-center">
            <Link to="/free-tools" className="text-[#2F80ED] font-medium hover:underline flex items-center gap-1 justify-center">
              See All Free Tools <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Section 7 — Standards Alignment */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4 text-center">
            STANDARDS ALIGNMENT
          </p>
          <h2 className="text-[32px] font-bold text-[#102A43] mb-12 text-center">
            Aligned with where regulation is headed
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div>
              <h3 className="font-bold text-[#102A43] mb-2">EU AI Act</h3>
              <p className="text-[14px] text-[#64748B]">
                Organizations deploying AI in or affecting the EU must document risk, oversight, and accountability.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-[#102A43] mb-2">NIST AI RMF</h3>
              <p className="text-[14px] text-[#64748B]">
                The US framework for managing AI risk across sectors — AASC maps directly to its governance requirements.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-[#102A43] mb-2">ISO/IEC 42001</h3>
              <p className="text-[14px] text-[#64748B]">
                The international standard for AI management systems — AASC aligns with its documentation and annual review cycle.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-[#102A43] mb-2">OECD AI Principles</h3>
              <p className="text-[14px] text-[#64748B]">
                Global principles for responsible AI use — AASC is the practical implementation layer.
              </p>
            </div>
          </div>
          
          <p className="text-[12px] text-[#94A3B8] text-center max-w-3xl mx-auto">
            AASC is not a law firm and does not provide legal advice. AASC is the operational framework organizations use to prepare for AI governance expectations.
          </p>
        </div>
      </section>

      {/* Section 8 — Declaration */}
      <section className="bg-[#102A43] py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4">
            ACCOUNTABILITY DECLARATION
          </p>
          <h2 className="text-[30px] font-bold text-white mb-6 leading-tight">
            Publicly verifiable proof that your organization uses AI responsibly
          </h2>
          <p className="text-[15px] text-[#94A3B8] mb-8 max-w-2xl mx-auto leading-relaxed">
            Organizations that complete the AASC Accountability Declaration are listed in the public registry, receive an embeddable accountability badge, and renew annually. Any client, auditor, or procurement team can verify your status instantly.
          </p>
          <p className="text-[40px] font-bold text-white mb-8">
            $997 / year
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-8 mb-10">
            <div className="flex items-center gap-2 text-white">
              <List size={20} />
              <span className="text-[14px]">Public registry listing</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <Shield size={20} />
              <span className="text-[14px]">Embeddable accountability badge</span>
            </div>
            <div className="flex items-center gap-2 text-white">
              <RefreshCw size={20} />
              <span className="text-[14px]">Annual renewal keeps your declaration current</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/apply" 
              className="bg-[#2F80ED] text-white font-semibold px-8 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors"
            >
              Start Your Declaration
            </Link>
            <Link 
              to="/registry" 
              className="text-[#94A3B8] hover:text-white underline transition-colors"
            >
              View the Registry
            </Link>
          </div>
        </div>
      </section>

      {/* Section 9 — Who It's For */}
      <section className="bg-[#F9FAFB] py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-bold text-[#2F80ED] uppercase tracking-[1.2px] mb-4 text-center">
            WHO IT'S FOR
          </p>
          <h2 className="text-[32px] font-bold text-[#102A43] mb-12 text-center">
            Built for any organization that uses AI
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {[
              'SaaS Companies',
              'Agencies',
              'Consultants',
              'Healthcare',
              'Education',
              'Financial Services',
              'Contractors',
              'Professional Services',
              'Internal AI Teams',
              'Government'
            ].map((industry) => (
              <div 
                key={industry}
                className="bg-white border border-[#E2E8F0] rounded-[8px] py-4 px-5 text-center"
              >
                <span className="text-[14px] font-medium text-[#102A43]">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 10 — Final CTA */}
      <section className="bg-[#102A43] py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-[32px] font-bold text-white mb-4">
            Know where you stand.
          </h2>
          <p className="text-[16px] text-[#94A3B8] mb-8">
            Get your free AI Accountability Score in under two minutes.
          </p>
          <Link 
            to="/assessment/form" 
            className="inline-block bg-[#2F80ED] text-white font-semibold px-8 py-3 rounded-[7px] hover:bg-[#2563eb] transition-colors mb-4"
          >
            Get Your Score
          </Link>
          <p className="text-[12px] text-[#64748B]">
            Free · no signup required · instant results
          </p>
        </div>
      </section>

      {/* Footer */}
      <MainFooter />
    </div>
  );
}

export default Home;
